import { useTranslation } from "react-i18next";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

const BRAND_CARD_META = [
  { icon: "ri-quill-pen-line", featureCount: 4, image: "https://public.readdy.ai/ai/img_res/945458cb21dc71d5c8643cfa9054eaeb.jpg" },
  { icon: "ri-palette-line",   featureCount: 4, image: "https://readdy.ai/api/search-image?query=visual%20identity%20system%20brand%20guidelines%20book%20design%2C%20color%20palette%20swatches%20typography%20scale%2C%20brand%20style%20guide%20mockup%2C%20elegant%20premium%20design%20system%20documentation%2C%20warm%20neutral%20tones&width=480&height=260&seq=brand102&orientation=landscape" },
  { icon: "ri-newspaper-line", featureCount: 4, image: "https://readdy.ai/api/search-image?query=brand%20application%20design%20mockups%2C%20business%20cards%20letterhead%20brochure%20stationery%20set%2C%20professional%20brand%20collateral%20flat%20lay%2C%20elegant%20black%20and%20gold%20branding%20materials%2C%20luxury%20brand%20identity%20presentation&width=480&height=260&seq=brand103&orientation=landscape" },
];

const PROCESS_ICONS = [
  "ri-search-2-line",
  "ri-lightbulb-line",
  "ri-edit-2-line",
  "ri-gift-line",
];

export default function BrandIdentitySection() {
  const { t } = useTranslation();
  const { ref: cardsRef, isVisible: cardsVis } = useScrollAnimation(0.08);
  const { ref: processRef, isVisible: processVis } = useScrollAnimation(0.08);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="brand" className="py-28 bg-[#F7F5F2]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#C9A96E] text-sm font-semibold tracking-widest uppercase">{t("br.eyebrow")}</span>
          <h2 className="text-4xl xl:text-5xl font-bold text-[#0C0C0C] mt-3 mb-5 leading-tight">
            {t("br.title")}
          </h2>
          <p className="text-[#0C0C0C]/50 text-lg max-w-xl mx-auto leading-relaxed">
            {t("br.sub")}
          </p>
        </div>

        {/* 3 Service Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-20">
          {BRAND_CARD_META.map((card, i) => (
            <div
              key={i}
              className={`group bg-white rounded-3xl overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer ${cardsVis ? `anim-fade-up anim-d-${(i + 1) * 100}` : "opacity-0"}`}
            >
              <div className="w-full h-44 overflow-hidden">
                <img src={card.image} alt={t(`br.c${i}.title`)} className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-7">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C8953A]/10 group-hover:bg-[#C8953A]/20 mb-4 transition-colors duration-300">
                  <i className={`${card.icon} text-[#C8953A] text-lg`} />
                </div>
                <h3 className="text-[#0C0C0C] font-bold text-xl mb-3">{t(`br.c${i}.title`)}</h3>
                <p className="text-[#0C0C0C]/50 text-sm leading-relaxed mb-5">{t(`br.c${i}.desc`)}</p>
                <div className="space-y-2">
                  {Array.from({ length: card.featureCount }, (_, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#C8953A] flex-shrink-0" />
                      <span className="text-[#0C0C0C]/55 text-xs">{t(`br.c${i}.f${j}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 4-Step Process */}
        <div ref={processRef} className="bg-[#0C0C0C] rounded-3xl p-10 xl:p-14">
          <div className={`text-center mb-12 ${processVis ? "anim-fade-up" : "opacity-0"}`}>
            <span className="text-[#C8953A] text-sm font-semibold tracking-widest uppercase">{t("br.proc.eyebrow")}</span>
            <h3 className="text-3xl xl:text-4xl font-bold text-white mt-3">{t("br.proc.title")}</h3>
            <p className="text-white/40 text-sm mt-3 max-w-md mx-auto">{t("br.proc.sub")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {PROCESS_ICONS.map((icon, i) => (
              <div key={i} className={`relative ${processVis ? `anim-fade-up anim-d-${(i + 1) * 100}` : "opacity-0"}`}>
                <div className="bg-white/5 border border-white/8 rounded-2xl p-6 relative z-10 hover:border-[#C8953A]/30 hover:bg-white/8 transition-all duration-300 group cursor-pointer">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C8953A]/10 group-hover:bg-[#C8953A]/20 transition-colors duration-300">
                      <i className={`${icon} text-[#C8953A] text-xl`} />
                    </div>
                    <span className="text-5xl font-bold text-white/5 group-hover:text-white/8 transition-colors leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h4 className="text-white font-bold text-base mb-2">{t(`br.proc${i}.title`)}</h4>
                  <p className="text-white/40 text-xs leading-relaxed">{t(`br.proc${i}.desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`text-center mt-10 ${processVis ? "anim-fade-up anim-d-500" : "opacity-0"}`}>
            <button
              onClick={() => scrollTo("contact")}
              className="whitespace-nowrap bg-[#C8953A] hover:bg-[#a97b30] text-[#0C0C0C] font-semibold px-10 py-4 rounded-full text-sm transition-all duration-200 cursor-pointer"
            >
              {t("br.proc.cta")}
              <i className="ri-arrow-right-line ml-2" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
